import { NavLink } from 'react-router-dom';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState, useEffect } from 'react';

export interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
}

export interface NavGroup {
  id: string;
  type: 'single' | 'group';
  label?: string;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  badge?: number;
  items: NavItem[];
}

interface CollapsibleNavProps {
  groups: NavGroup[];
  onNavigate?: () => void;
}

export function CollapsibleNav({ groups, onNavigate }: CollapsibleNavProps) {
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    const saved = localStorage.getItem('nav-open-groups');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    // Default: security group open
    return groups.filter(g => g.defaultOpen).map(g => g.id);
  });

  useEffect(() => {
    localStorage.setItem('nav-open-groups', JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (id: string) => {
    setOpenGroups(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const renderNavItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={onNavigate}
      aria-label={item.badge ? `${item.label} (${item.badge} ${item.badge === 1 ? 'alert' : 'alerts'})` : item.label}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 relative overflow-hidden group touch-manipulation min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          isActive
            ? 'bg-gradient-to-r from-[hsl(216_100%_50%/0.15)] to-[hsl(196_100%_50%/0.05)] text-primary border-l-3 border-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:translate-x-1'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(216_100%_50%/0.1)] to-transparent" aria-hidden="true" />
          )}
          <item.icon
            className={cn(
              'h-4 w-4 relative z-10 transition-all duration-200 flex-shrink-0',
              isActive && 'text-primary drop-shadow-[0_0_8px_hsl(216_100%_50%/0.5)]'
            )}
            aria-hidden="true"
          />
          <span className="relative z-10 flex-1 truncate">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span
              className="ml-auto relative z-10 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground shadow-sm flex-shrink-0"
              aria-hidden="true"
            >
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <nav className="space-y-1">
      {groups.map(group => {
        if (group.type === 'single') {
          return group.items.map(renderNavItem);
        }

        const isOpen = openGroups.includes(group.id);
        const Icon = group.icon;

        return (
          <Collapsible
            key={group.id}
            open={isOpen}
            onOpenChange={() => toggleGroup(group.id)}
          >
            <CollapsibleTrigger
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all group touch-manipulation min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={`${group.label} navigation group`}
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-3">
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
                <span className="truncate">{group.label}</span>
                {group.badge !== undefined && group.badge > 0 && (
                  <span
                    className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground shadow-sm flex-shrink-0"
                    aria-label={`${group.badge} ${group.badge === 1 ? 'alert' : 'alerts'}`}
                  >
                    {group.badge > 99 ? '99+' : group.badge}
                  </span>
                )}
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform duration-200 flex-shrink-0',
                  isOpen && 'rotate-180'
                )}
                aria-hidden="true"
              />
              <span className="sr-only">
                {isOpen ? 'Collapse' : 'Expand'} {group.label}
              </span>
            </CollapsibleTrigger>

            <CollapsibleContent
              className="pl-6 space-y-1 mt-1"
              aria-label={`${group.label} items`}
            >
              {group.items.map(renderNavItem)}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </nav>
  );
}
